// ============================================================
// useDebouncedResult - a heavy pure computation, run once the input has
// held still for a moment.
//
// The forecasts (useProbability, useDefenceProbability,
// useFiringSolutions) are Monte Carlo runs over the live attack state,
// which changes on every keystroke of a modifier. Each had its own copy
// of this timer; the third copy made it a hook.
// ============================================================
import { useEffect, useState } from 'react';

/** How long the input must hold still before the computation runs. */
const FORECAST_DEBOUNCE_MS = 300;

/**
 * `compute(input)` after `delayMs` of no change, with `loading` true while
 * it waits. A null input clears the result at once and computes nothing.
 *
 * `input` must change identity exactly when the computation should re-run
 * - build it in a useMemo over the fields it reads. `compute` should be a
 * stable function (a module import), or every render restarts the wait.
 */
export function useDebouncedResult<I, R>(
  input: I | null,
  compute: (input: I) => R,
  delayMs = FORECAST_DEBOUNCE_MS,
): { result: R | null; loading: boolean } {
  const [result, setResult] = useState<R | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (input === null) {
      setResult(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    // The cleanup cancels a wait the next change supersedes: that is the debounce.
    const timer = setTimeout(() => {
      setResult(compute(input));
      setLoading(false);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [input, compute, delayMs]);
  return { result, loading };
}
